import esriRequest from "@arcgis/core/request.js";
import { showAlert } from "../../utils/Helpers";
import fetchDataWithAuth from "../../utils/api";
import { getLocalStorageItem } from "../../utils/HelperFunctions";
import { useEDPContext } from "../../components/EDPContext";

const CommonFunctions = (params) => {

    const {
        setInsectsDomainValues,
        setPlantsDomainValues,
        setMammalsDomainValues,
        setReptilesDomainValues,
        setSpeciesOrder,
        setFamilyName,
        setMammalsSpeciesNames,
        setReptileSpeciesNames,
        t,
        setLoading
    } = params;

    //const portalToken = getUserTokenFromStorage();
    const portalToken = localStorage.getItem("PortalToken");
    const { globalDataContextValue } = useEDPContext();
    const { globalData, setGlobalData } = globalDataContextValue;
    var AnimalSightingTab = globalData.find(module => module.moduleName === "MWQ")
        ?.subTabs.find(tab => tab.name === "Animal Sighting");
    const contextFilters = AnimalSightingTab?.filters;

    const GetInsectsDomainValues = async () => {

        try {
            const options = {
                //query: { f: "json" },
                query: { f: "json", token: portalToken },
                responseType: "json",
            };
            const response = await esriRequest(window.InvertiblesInfo.Url, options);
            const fieldsWithDomains = {};

            // Extract fields with domains
            response.data.fields.forEach((field) => {
                if (field.domain) {
                    fieldsWithDomains[field.name] = field.domain;
                }
            });

            let DisplayAttributes = window.InvertiblesInfo.DisplayAttributes;
            const specimentakenArray = [];
            const habitatArray = [];
            const habitatStatusArray = [];
            const collectionMethodArray = [];
            const speciesnameArray = [];
            const insectOrderArray = [];
            const familyArray = [];
            const InsectOrderMap = {};
            const FamilyDataMap = {};
            const InsectsFamilyDataType = {};

            // Helper function to get the correct array based on the attribute name
            const getTargetArray = (attrName) => {
                switch (attrName) {
                    case "SpecimenTaken":
                        return specimentakenArray;
                    case "SpeciesName":
                        return speciesnameArray;
                    case "Habitat":
                        return habitatArray;
                    case "HabitatStatus":
                        return habitatStatusArray;
                    case "TrappingMethod":
                        return collectionMethodArray;
                    default:
                        return [];
                }
            };

            // Iterate over types and populate insect orders and families
            response.data.types.forEach((type) => {
                InsectOrderMap[type.id] = type.name.trim();
                insectOrderArray.push({
                    key: `${type.id}`,
                    value: `${type.name.trim()}`,
                });

                const familyObject = {};

                const familyDomain = type.domains?.Family;
                if (familyDomain) {
                    const isInherited = familyDomain.type === "inherited";
                    const domainValues = isInherited
                        ? fieldsWithDomains.Family.codedValues
                        : familyDomain.codedValues;

                    domainValues.forEach((value) => {
                        const { code, name } = value;
                        FamilyDataMap[code] = name;
                        familyArray.push({ key: `${code}`, value: `${name}` });
                        familyObject[code] = name;
                    });
                }
                InsectsFamilyDataType[type.id] = familyObject;
            });

            // Map display attributes to domain values
            DisplayAttributes.forEach(({ AttrName }) => {
                const domain = fieldsWithDomains[AttrName];

                if (domain && domain.codedValues) {
                    const targetArray = getTargetArray(AttrName);
                    domain.codedValues.forEach(({ code, name }) => {
                        targetArray.push({
                            key: `${code.trim()}`,
                            value: `${name.trim()}`,
                        });
                    });
                }
            });
            const newDomainValue1 = {
                SpecimenTaken: specimentakenArray,
                SpeciesName: speciesnameArray,
                InsectOrder: insectOrderArray,
                Family: familyArray,
                Habitat: habitatArray,
                HabitatStatus: habitatStatusArray,
                TrappingMethod: collectionMethodArray,
            };
            const newDomainValue2 = {
                Status: specimentakenArray,
                SpeciesName: speciesnameArray,
                SpeciesOrder: insectOrderArray,
                SpeciesFamily: familyArray,
                Habitat: habitatArray,
                HabitatStatus: habitatStatusArray,
                MethodUsed: collectionMethodArray,
            };

            const mergedDomainValue = {
                SpecimenTaken: newDomainValue1.SpecimenTaken || newDomainValue2.Status, // Combine similar fields
                Status: newDomainValue2.Status || newDomainValue1.SpecimenTaken,
                SpeciesName: newDomainValue1.SpeciesName, // Same field
                InsectOrder: newDomainValue1.InsectOrder || newDomainValue2.SpeciesOrder, // Combine similar fields
                SpeciesOrder: newDomainValue2.SpeciesOrder || newDomainValue1.InsectOrder,
                Family: newDomainValue1.Family || newDomainValue2.SpeciesFamily, // Combine similar fields
                SpeciesFamily: newDomainValue2.SpeciesFamily || newDomainValue1.Family,
                Habitat: newDomainValue1.Habitat, // Same field
                HabitatStatus: newDomainValue1.HabitatStatus, // Same field
                TrappingMethod: newDomainValue1.TrappingMethod || newDomainValue2.MethodUsed, // Combine similar fields
                MethodUsed: newDomainValue2.MethodUsed || newDomainValue1.TrappingMethod,
            };

            // setInsectsDomainValues((prev) => [...prev, mergedDomainValue]);
            setInsectsDomainValues((prev) => {
                if (!prev.some((item) => JSON.stringify(item) === JSON.stringify(mergedDomainValue))) {
                    return [...prev, mergedDomainValue];
                }
                return prev; // Prevent duplicate entries
            });

            setSpeciesOrder(insectOrderArray);
            setFamilyName(familyArray);
            setGlobalData((prev) =>
                prev.map((module) =>
                    module.moduleName === "Animal Sighting"
                        ? {
                            ...module,
                            subTabs: module.subTabs.map((subTab) =>
                                subTab.name === "PendingRecords"
                                    ? {
                                        ...subTab,
                                        data: {
                                            ...subTab.data, // Retaining existing data
                                            InsectsDomainValues: mergedDomainValue, // Prevents duplicates
                                            SpeciesOrder: insectOrderArray,
                                            FamilyName: familyArray
                                        }
                                    } // Clears existing data and sets new data
                                    : subTab
                            ),
                        }
                        : module
                )
            );
        } catch (error) {
            console.log("Error query featrues:", error);
            showAlert(t("faileddomain"), "error");
        }
    };
    const GetMammalsDomainValues = async () => {

        try {
            const options = {
                //query: { f: "json" },
                query: { f: "json", token: portalToken },
                responseType: "json",
            };
            const response = await esriRequest(window.MammalsInfo.Url, options);
            const fieldsWithDomains = {};

            // Extract fields with domains
            response.data.fields.forEach((field) => {
                if (field.domain) {
                    fieldsWithDomains[field.name] = field.domain;
                }
            });

            let DisplayAttributes = window.MammalsInfo.DisplayAttributes;
            const sighttypeArray = [];
            const habitatArray = [];
            const species = [];
            const speciesNames = [];
            const SpeciesData = {};
            const SpeciesNamesData = {};
            const SpecieswithSpeciesNamesData = [];

            // Helper function to get the correct array based on the attribute name
            const getTargetArray = (attrName) => {
                switch (attrName) {
                    case "SightingType":
                        return sighttypeArray;
                    case "Habitat":
                        return habitatArray;
                    default:
                        return [];
                }
            };
            // Iterate over types and populate insect orders and families
            response.data.types.forEach((type) => {
                SpeciesData[type.id] = type.name;
                species.push({ key: `${type.id}`, value: `${type.name}` });

                const specnamesobject = [];

                const speciesnamesdomain = type.domains?.SpeciesName;
                if (speciesnamesdomain) {
                    const isInherited = speciesnamesdomain.type === "inherited";
                    const domainValues = isInherited
                        ? fieldsWithDomains.SpeciesName.codedValues
                        : speciesnamesdomain.codedValues;

                    domainValues.forEach((value) => {
                        const { code, name } = value;
                        SpeciesNamesData[code] = name;
                        speciesNames.push({ key: `${code}`, value: `${name}` });
                        //specnamesobject[code] = name;
                        specnamesobject.push({ key: `${code}`, value: `${name}` });
                    });
                }
                //SpecieswithSpeciesNamesData[type.id] = specnamesobject;
                SpecieswithSpeciesNamesData.push({
                    Species: type.name, // Correct object syntax
                    SpeciesNames: specnamesobject, // Correct object syntax
                });
            });
            // Map display attributes to domain values
            DisplayAttributes.forEach(({ AttrName }) => {
                const domain = fieldsWithDomains[AttrName];

                if (domain && domain.codedValues) {
                    const targetArray = getTargetArray(AttrName);
                    domain.codedValues.forEach(({ code, name }) => {
                        targetArray.push({
                            key: `${code.trim()}`,
                            value: `${name.trim()}`,
                        });
                    });
                }
            });
            const newDomainValue = {
                SightingType: sighttypeArray,
                Habitat: habitatArray,
                Species: species,
                SpeciesName: speciesNames,
            };
            // setMammalsDomainValues((prev) => [...prev, newDomainValue]);
            setMammalsDomainValues((prev) => {
                if (!prev.some((item) => JSON.stringify(item) === JSON.stringify(newDomainValue))) {
                    return [...prev, newDomainValue];
                }
                return prev; // Prevent duplicate entries
            });
            setMammalsSpeciesNames(SpecieswithSpeciesNamesData);
            setGlobalData((prev) =>
                prev.map((module) =>
                    module.moduleName === "Animal Sighting"
                        ? {
                            ...module,
                            subTabs: module.subTabs.map((subTab) =>
                                subTab.name === "PendingRecords"
                                    ? {
                                        ...subTab,
                                        data: {
                                            ...subTab.data, // Retaining existing data
                                            MammalsDomainValues: newDomainValue, // Prevents duplicates
                                            MammalsSpeciesNames: SpecieswithSpeciesNamesData
                                        }
                                    } // Clears existing data and sets new data
                                    : subTab
                            ),
                        }
                        : module
                )
            );
        } catch (error) {
            console.log("Error query featrues:", error);
            showAlert(t("faileddomain"), "error");
        }
    };
    const GetPlantsDomainValues = async () => {
        try {
            const options = {
                //query: { f: "json" },
                query: { f: "json", token: portalToken },
                responseType: "json",
            };
            const response = await esriRequest(window.PlantInfo.Url, options);
            const fieldsWithDomains = {};

            // Extract fields with domains
            response.data.fields.forEach((field) => {
                if (field.domain) {
                    fieldsWithDomains[field.name] = field.domain;
                }
            });

            let DisplayAttributes = window.PlantInfo.DisplayAttributes;
            const plantsArray = [];
            const phenologyArray = [];
            const habitatArray = [];
            const GrazingArray = [];
            const PrecipitaionArray = [];
            const percentagecoverArray = [];
            const specimenArray = [];

            // Helper function to get the correct array based on the attribute name
            const getTargetArray = (attrName) => {
                switch (attrName) {
                    case "PlantSpecies":
                        return plantsArray;
                    case "Phenology":
                        return phenologyArray;
                    case "Habitat":
                        return habitatArray;
                    case "GrazingSigns":
                        return GrazingArray;
                    case "Precipitation":
                        return PrecipitaionArray;
                    case "SpecimenCollected":
                        return specimenArray;
                    default:
                        return [];
                }
            };

            // Map display attributes to domain values
            DisplayAttributes.forEach(({ AttrName }) => {
                const domain = fieldsWithDomains[AttrName];

                if (domain && domain.codedValues) {
                    const targetArray = getTargetArray(AttrName);
                    domain.codedValues.forEach(({ code, name }) => {
                        targetArray.push({
                            key: `${code.trim()}`,
                            value: `${name.trim()}`,
                        });
                    });
                } else if (domain && domain.range) {
                    if (AttrName === "PercentageCover") {
                        let minrange = domain.range[0];
                        let maxrange = domain.range[1];
                        for (let j = minrange; j <= maxrange; j++) {
                            //percentagecoverArray.push(j + ":" + j);
                            percentagecoverArray.push({ key: j, value: j });
                        }
                    }
                }
            });
            const newDomainValue = {
                PlantSpecies: plantsArray,
                Phenology: phenologyArray,
                Habitat: habitatArray,
                GrazingSigns: GrazingArray,
                Precipitation: PrecipitaionArray,
                PercentageCover: percentagecoverArray,
                SpecimenCollected: specimenArray,
            };
            // setPlantsDomainValues((prev) => [...prev, newDomainValue]);
            setPlantsDomainValues((prev) => {
                if (!prev.some((item) => JSON.stringify(item) === JSON.stringify(newDomainValue))) {
                    return [...prev, newDomainValue];
                }
                return prev; // Prevent duplicate entries
            });
            setGlobalData((prev) =>
                prev.map((module) =>
                    module.moduleName === "Animal Sighting"
                        ? {
                            ...module,
                            subTabs: module.subTabs.map((subTab) =>
                                subTab.name === "PendingRecords"
                                    ? {
                                        ...subTab,
                                        data: {
                                            ...subTab.data, // Retaining existing data
                                            PlantsDomainValues: newDomainValue, // Prevents duplicates

                                        }
                                    } // Clears existing data and sets new data
                                    : subTab
                            ),
                        }
                        : module
                )
            );
        } catch (error) {
            console.log("Error query featrues:", error);
            showAlert(t("faileddomain"), "error");
        }
    };
    const GetReptilesDomainValues = async () => {
        try {
            const options = {
                //query: { f: "json" },
                query: { f: "json", token: portalToken },
                responseType: "json",
            };
            const response = await esriRequest(window.ReptilesInfo.Url, options);
            const fieldsWithDomains = {};
            // Extract fields with domains
            response.data.fields.forEach((field) => {
                if (field.domain) {
                    fieldsWithDomains[field.name] = field.domain;
                }
            });
            let DisplayAttributes = window.ReptilesInfo.DisplayAttributes;
            const reptilesArray = [];
            const sightingArray = [];
            const speciesArray = [];
            const habitatArray = [];
            const microhabitatArray = [];
            const ReptilesData = {};
            const SpeciesData = {};
            const ReptilesSpeciesDataType = [];

            // Helper function to get the correct array based on the attribute name
            const getTargetArray = (attrName) => {
                switch (attrName) {
                    case "SightingType":
                        return sightingArray;
                    case "Habitat":
                        return habitatArray;
                    case "MicroHabitat":
                        return microhabitatArray;
                    default:
                        return [];
                }
            };

            response.data.types.forEach((type) => {
                ReptilesData[type.id] = type.name;
                reptilesArray.push({ key: `${type.id}`, value: `${type.name}` });

                const specobject = [];

                const speciesdomain = type.domains?.Species;
                if (speciesdomain) {
                    const isInherited = speciesdomain.type === "inherited";
                    const domainValues = isInherited
                        ? fieldsWithDomains.Species.codedValues
                        : speciesdomain.codedValues;

                    domainValues.forEach((value) => {
                        const { code, name } = value;
                        SpeciesData[code] = name;
                        speciesArray.push({ key: `${code}`, value: `${name}` });
                        //specobject[code] = name;
                        specobject.push({ key: `${code}`, value: `${name}` });
                    });
                }
                //ReptilesSpeciesDataType[type.id] = specobject;
                ReptilesSpeciesDataType.push({
                    Reptile: type.name, // Correct object syntax
                    Species: specobject, // Correct object syntax
                });
            });

            // Map display attributes to domain values
            DisplayAttributes.forEach(({ AttrName }) => {
                const domain = fieldsWithDomains[AttrName];
                if (domain && domain.codedValues) {
                    const targetArray = getTargetArray(AttrName);
                    domain.codedValues.forEach(({ code, name }) => {
                        targetArray.push({
                            key: `${code.trim()}`,
                            value: `${name.trim()}`,
                        });
                    });
                }
            });
            const newDomainValue = {
                Reptiles: reptilesArray,
                Species: speciesArray,
                Habitat: habitatArray,
                MicroHabitat: microhabitatArray,
                SightingType: sightingArray,
            };
            // setReptilesDomainValues((prev) => [...prev, newDomainValue]);
            setReptilesDomainValues((prev) => {
                if (!prev.some((item) => JSON.stringify(item) === JSON.stringify(newDomainValue))) {
                    return [...prev, newDomainValue];
                }
                return prev; // Prevent duplicate entries
            });
            setReptileSpeciesNames(ReptilesSpeciesDataType);

            setGlobalData((prev) =>
                prev.map((module) =>
                    module.moduleName === "Animal Sighting"
                        ? {
                            ...module,
                            subTabs: module.subTabs.map((subTab) =>
                                subTab.name === "PendingRecords"
                                    ? {
                                        ...subTab,
                                        data: {
                                            ...subTab.data, // Retaining existing data
                                            ReptilesDomainValues: newDomainValue, // Prevents duplicates
                                            ReptileSpeciesNames: ReptilesSpeciesDataType
                                        }
                                    } // Clears existing data and sets new data
                                    : subTab
                            ),
                        }
                        : module
                )
            );
        } catch (error) {
            console.log("Error query featrues:", error);
            showAlert(t("faileddomain"), "error");
        }
    };
    const GetDomainValues = async () => {
        setLoading(true);
        try {
            await Promise.all([
                GetInsectsDomainValues(),
                GetPlantsDomainValues(),
                GetMammalsDomainValues(),
                GetReptilesDomainValues(),
            ]);
        } catch (error) {
            console.log(error);
        }
        //  finally {
        //     //setLoading(false);
        // }
    };

    const getUserTokenFromStorage = () => {
        const storedToken = getLocalStorageItem("PortalToken");
        return storedToken ? storedToken.trim() || null : null;
    };

           return {
        GetDomainValues,
    };
}

export default CommonFunctions;